import { Icon } from '@eb/eds-icon';
import { ArrowRightChunky } from '@eb/eds-iconography';
import { isEmpty } from 'lodash';
import React from 'react';
import {
    CUSTOM_PRICING,
    DEFAULT_PACKAGE_TYPES,
    formatPricing,
    PackageTypes,
    PACKAGE_ONE,
    PACKAGE_THREE,
    PACKAGE_TWO,
    PricingInterface,
    pricingModulePackageDefaults,
    PRICING_PATH,
} from '../constants/pricing';
import { relativeZindexStyle } from './utils';

interface PackageInterface {
    subtext: string;
    pricing: PricingInterface | null;
}

interface PackagesInterface {
    package1: PackageInterface;
    package2: PackageInterface;
    package3: PackageInterface;
}

interface PackageDetailsProps {
    feePerPaidTicketText: string;
    feePerFreeTicketText: string;
    ticketTypesText: string;
    price: string | null;
    hasBothPackagesPricing: boolean;
}

interface PackageComponentInterface extends PackageDetailsProps {
    subtext: string;
    name: string;
    shouldDisplayPackagesDetails: boolean;
    shouldIncludePackagesLinks: boolean;
}

export interface PricingProps {
    title: string;
    titleSubtext: string;
    packages: PackagesInterface;
    buttonText: string;
    env: {
        localeInfo: { locale: string };
        currencyFormat: string;
    };
    shouldDisplayPackagesDetails: boolean;
    shouldIncludePackagesLinks: boolean;
}

export const PackageDetailsComponent = (details: PackageDetailsProps) => {
    const {
        feePerPaidTicketText,
        feePerFreeTicketText,
        ticketTypesText,
        price,
        hasBothPackagesPricing,
    } = details;
    return (
        <>
            <div className="eds-l-pad-vert-4">
                <hr className="feed-events-bucket__separator " />
            </div>
            {hasBothPackagesPricing && (
                <>
                    <p
                        data-spec="packageFees"
                        className="eds-text-bm eds-text-color--grey-700 eds-l-pad-bot-1"
                    >
                        {`${feePerPaidTicketText} ${price}`}
                    </p>
                    <p
                        data-spec="packageFees"
                        className="eds-text-bm eds-text-color--grey-700 eds-l-pad-bot-1"
                    >
                        {feePerFreeTicketText}
                    </p>
                </>
            )}
            <p className="eds-text-bm eds-text-color--grey-700 eds-l-pad-bot-1">
                {ticketTypesText}
            </p>
        </>
    );
};

export const PackageComponent = (pckg: PackageComponentInterface) => {
    const {
        price,
        subtext,
        name,
        feePerPaidTicketText,
        feePerFreeTicketText,
        ticketTypesText,
        shouldIncludePackagesLinks,
        shouldDisplayPackagesDetails,
        hasBothPackagesPricing,
    } = pckg;
    const details = {
        price,
        feePerFreeTicketText,
        feePerPaidTicketText,
        ticketTypesText,
        hasBothPackagesPricing,
    };
    return (
        <div className="eds-g-cell eds-g-cell-sm-1-1 eds-g-cell-sw-1-3 eds-l-pad-all-6 eds-l-mar-bot-2">
            {shouldIncludePackagesLinks ? (
                <a
                    href={PRICING_PATH}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <h2 className="eds-text-hs eds-text-color--grey-900 eds-l-pad-bot-2">
                        {name}
                        <span>
                            <Icon
                                type={<ArrowRightChunky />}
                                size="small"
                                color="ui-orange"
                            />
                        </span>
                    </h2>
                </a>
            ) : (
                <h2 className="eds-text-hs eds-text-color--grey-900 eds-l-pad-bot-2">
                    {name}
                </h2>
            )}
            <p className="eds-text-bl eds-text-weight--regular eds-text-color--grey-700">
                {subtext}
            </p>
            {shouldDisplayPackagesDetails && (
                <PackageDetailsComponent {...details} />
            )}
        </div>
    );
};

const PricingModule = ({
    packages,
    buttonText,
    title,
    titleSubtext,
    env,
    shouldIncludePackagesLinks,
    shouldDisplayPackagesDetails,
}: PricingProps) => {
    const hasBothPackagesPricing =
        !isEmpty(packages[PACKAGE_ONE].pricing) &&
        !isEmpty(packages[PACKAGE_TWO].pricing);
    const getPricingPackage = (value: PackageTypes, idx: number) => {
        const {
            localeInfo: { locale },
            currencyFormat,
        } = env;
        const { pricing, subtext } = packages[value];
        const price =
            value === PACKAGE_THREE
                ? CUSTOM_PRICING
                : formatPricing(currencyFormat, locale, pricing);
        return (
            <PackageComponent
                key={idx}
                price={price?.toString() || null}
                subtext={subtext}
                shouldIncludePackagesLinks={shouldIncludePackagesLinks}
                shouldDisplayPackagesDetails={shouldDisplayPackagesDetails}
                hasBothPackagesPricing={hasBothPackagesPricing}
                {...pricingModulePackageDefaults[value]}
            />
        );
    };
    return (
        <section>
            <div className="marketing-container__max-width">
                <div
                    className="eds-g-grid eds-bg-color--grey-100 eds-l-pad-top-20"
                    style={relativeZindexStyle}
                >
                    <div className="eds-l-mar-top-2">
                        <div className="eds-g-cell eds-g-cell-12-12 eds-g-offset-0-12 eds-g-cell-md-8-12 eds-g-offset-md-2-12 eds-l-pad-hor-6">
                            <h2 className="eds-text-color--grey-900 eds-text-hm eds-text--center mktg-text-weight--heavy eds-text--center">
                                {title}
                            </h2>
                            <p className="eds-text-color--grey-700 eds-text-bl eds-text-weight--regular eds-l-mar-top-4 eds-text--center  eds-l-pad-bot-8">
                                {titleSubtext}
                            </p>
                        </div>
                        <div className=" eds-g-cell-12-12 eds-g-offset-0-12 eds-g-cell-md-10-12 eds-g-offset-md-1-12 ">
                            {DEFAULT_PACKAGE_TYPES.map((value, idx) =>
                                getPricingPackage(value, idx),
                            )}
                        </div>
                        <div className="eds-text--center eds-l-mar-bot-6">
                            <div className="eds-g-cell eds-g-cell-12-12 eds-l-mar-top-4 mktg-button-overlap">
                                <a
                                    href={PRICING_PATH}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <button
                                        className="eds-btn eds-btn--button eds-btn--fill eds-btn--responsive"
                                        type="button"
                                    >
                                        {buttonText}
                                    </button>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default PricingModule;
